import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {
  ContentSubmission,
  ContentSubmissionApproval,
  ContentSubmissionComment,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {ConversationMessageAttachment} from '@/types/conversationMessage';
import {APPROVAL_STEP_LABELS} from '@/views/campaigns/constants/approvalSteps';

export const FEED_ITEM_TYPE = {
  CREATOR_UPLOAD: 'creator_upload',
  STEP_APPROVAL: 'step_approval',
  ACTION: 'action',
  BRAND_MESSAGE: 'brand_message',
  COMMENT: 'comment',
  VERSION_MESSAGE: 'version_message',
} as const;

const ACTION_LABELS: Record<string, string> = {
  accepted: 'Content approved',
  rejected: 'Changes requested',
  archived: 'Content archived',
};

export type FeedItem = {
  id: string;
  type: (typeof FEED_ITEM_TYPE)[keyof typeof FEED_ITEM_TYPE];
  createdAt: string;
  formattedDate: string;
  versionNumber: number;
  content?: string;
  fullName?: string | null;
  brandMessageHtml?: string | null;
  step?: string;
  terminalState?: string;
  isArchiveNote?: boolean;
  isFromBrand?: boolean;
  attachments?: ConversationMessageAttachment[];
};

function aggregateActivityFeedItems(
  contentSubmission: ContentSubmission
): FeedItem[] {
  const {versions} = contentSubmission;
  if (!versions?.length) return [];

  const items: FeedItem[] = [];

  // Oldest version first (versions are newest-first from API, so reverse)
  const orderedVersions = versions.slice().reverse();

  orderedVersions.forEach((version, index) => {
    const versionNumber = index + 1;

    // Creator upload pill (all versions including V1)
    // submittedAt may be null for the initial version; fall back to createdAt
    const uploadTimestamp = version.submittedAt || version.createdAt;
    if (uploadTimestamp) {
      const dateStr = formattedDate(
        new Date(uploadTimestamp),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `creator-upload--${version.id}`,
        type: FEED_ITEM_TYPE.CREATOR_UPLOAD,
        createdAt: uploadTimestamp,
        formattedDate: dateStr,
        versionNumber,
        content: translate('Creator uploaded V{version} on {date}', {
          version: versionNumber,
          date: dateStr,
        }),
      });
    }

    // Internal comments
    (version.contentSubmissionComments ?? []).forEach(
      (comment: ContentSubmissionComment) => {
        items.push({
          id: comment.id,
          type: FEED_ITEM_TYPE.COMMENT,
          createdAt: comment.createdAt,
          formattedDate: formattedDate(
            new Date(comment.createdAt),
            MONTH_SHORT_DAY_YEAR_FORMAT
          ),
          versionNumber,
          content: comment.content,
          fullName:
            comment.dashboardUser?.displayName ||
            comment.fullName ||
            comment.email ||
            null,
          step: comment.step ?? undefined,
        });
      }
    );

    // Action event (approve / request changes / archive) — always shown when present
    if (version.terminalStateForRevision && version.feedbackProvidedAt) {
      const dateStr = formattedDate(
        new Date(version.feedbackProvidedAt),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      const label = translate(
        ACTION_LABELS[version.terminalStateForRevision] ??
          version.terminalStateForRevision
      );
      items.push({
        id: `action--${version.id}`,
        type: FEED_ITEM_TYPE.ACTION,
        createdAt: version.feedbackProvidedAt,
        formattedDate: dateStr,
        versionNumber,
        terminalState: version.terminalStateForRevision,
        content: translate('{label} V{version} on {date}', {
          label,
          version: versionNumber,
          date: dateStr,
        }),
      });
    }

    // Brand message — only shown when a message was actually sent.
    // If this is the latest version and the submission is currently pending,
    // the archive was reversed — don't show the leftover message.
    const isLatestVersion = index === orderedVersions.length - 1;
    const isArchiveNote =
      isLatestVersion && contentSubmission.aasmState === 'archived';
    const skipBrandMessage =
      isLatestVersion && contentSubmission.aasmState === 'pending';

    if (
      version.brandMessageHtml &&
      version.feedbackProvidedAt &&
      !skipBrandMessage
    ) {
      const dateStr = formattedDate(
        new Date(version.feedbackProvidedAt),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `brand-message--${version.id}`,
        type: FEED_ITEM_TYPE.BRAND_MESSAGE,
        createdAt: version.feedbackProvidedAt,
        formattedDate: dateStr,
        versionNumber,
        isArchiveNote,
        brandMessageHtml: version.brandMessageHtml,
        fullName:
          contentSubmission.campaign?.brand?.displayName ??
          contentSubmission.campaign?.brand?.name ??
          null,
      });
    }
  });

  // Step approvals — now per-version, so version number is known directly
  orderedVersions.forEach((version, index) => {
    const versionNumber = index + 1;
    (version.approvals ?? []).forEach((approval: ContentSubmissionApproval) => {
      const stepLabel = translate(
        APPROVAL_STEP_LABELS[approval.step] ?? approval.step
      );
      const dateStr = formattedDate(
        new Date(approval.createdAt),
        MONTH_SHORT_DAY_YEAR_FORMAT
      );
      items.push({
        id: `step-approval--${approval.id}`,
        type: FEED_ITEM_TYPE.STEP_APPROVAL,
        createdAt: approval.createdAt,
        formattedDate: dateStr,
        versionNumber,
        step: approval.step,
        content: translate('{step} approved V{version} on {date}', {
          step: stepLabel,
          version: versionNumber,
          date: dateStr,
        }),
      });
    });
  });

  // Oldest first — chat-style ordering: latest item appears at the bottom of the feed.
  return items.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

export default aggregateActivityFeedItems;
